import { createPortal } from 'react-dom';
import { FC, RefObject } from 'react';
import classNames from 'classnames';

export interface ActionMenuPopupItem {
  label: string;
  /** Called when the item is chosen. */
  onClick: () => void;
  /** `danger` paints the item red — for destructive actions. */
  variant?: 'default' | 'danger';
}

export interface ActionMenuPopupProps {
  items: ActionMenuPopupItem[];
  /** Offsets from the viewport's bottom and right edges, usually from the trigger's getBoundingClientRect. */
  position: { bottom: number; right: number };
  /** Ref to the menu container, so the caller can detect clicks outside it. */
  contentRef?: RefObject<HTMLDivElement | null>;
  /** Menu width in pixels. */
  width?: number;
  className?: string;
}

/** A floating action menu: a white panel of items, rendered into document.body through a portal. */
export const ActionMenuPopup: FC<ActionMenuPopupProps> = ({ items, position, contentRef, width = 260, className }) => {
  if (items.length === 0) return null;

  return createPortal(
    <div
      ref={contentRef as RefObject<HTMLDivElement>}
      className={classNames('fixed z-popover rounded-control bg-surface p-4 shadow-raised cursor-pointer', className)}
      style={{
        bottom: position.bottom,
        right: position.right,
        width,
      }}
      role="menu"
    >
      {items.map((item, index) => (
        <button
          key={index}
          type="button"
          role="menuitem"
          className={classNames(
            'w-full px-4 py-4 text-left text-ui cursor-pointer transition-colors',
            item.variant === 'danger' ? 'text-danger hover:bg-danger-surface' : 'text-ink hover:bg-surface-muted',
          )}
          onClick={item.onClick}
        >
          {item.label}
        </button>
      ))}
    </div>,
    document.body,
  );
};
